import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AlertTriangle, X } from "lucide-react";

/**
 * Shows a sticky banner at the top of ops-side pages when any trip's e-way bill
 * is expired or expiring within 2 days. Polled every 60s.
 */
export function EwayBanner() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const isOps = ["loading_supervisor", "accounts", "super_admin", "admin"].includes(user?.role);

  useEffect(() => {
    if (!isOps) return;
    let active = true;
    const fetch = async () => {
      try {
        const { data } = await api.get("/alerts/eway");
        if (active) setData(data);
      } catch (e) { /* silent */ }
    };
    fetch();
    const t = setInterval(fetch, 60_000);
    return () => { active = false; clearInterval(t); };
  }, [isOps]);

  if (!isOps || dismissed || !data) return null;
  const totalExpired = data.expired?.length || 0;
  const totalSoon = data.expiring_soon?.length || 0;
  if (!totalExpired && !totalSoon) return null;

  const severe = totalExpired > 0;
  return (
    <div data-testid="eway-banner"
      className={`${severe ? "bg-rose-900/40 border-rose-500/60 text-rose-200" : "bg-amber-900/30 border-amber-500/60 text-amber-200"} border-b px-6 py-3`}>
      <div className="max-w-7xl mx-auto flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1">
          <AlertTriangle className={`w-5 h-5 mt-0.5 ${severe ? "text-rose-300" : "text-amber-300"}`} />
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm">
              {severe && <>⚠ {totalExpired} e-way bill{totalExpired > 1 ? "s" : ""} already EXPIRED.</>}
              {!severe && <>⏳ {totalSoon} e-way bill{totalSoon > 1 ? "s" : ""} expiring in ≤2 days.</>}
            </div>
            <div className="text-xs mt-1 flex flex-wrap gap-x-4 gap-y-1" data-testid="eway-banner-list">
              {[...(data.expired || []), ...(data.expiring_soon || [])].slice(0, 6).map(t => (
                <Link key={t.id} to={`/trips/${t.id}`} className="hover:underline font-mono">
                  {t.cn_number} · {t.eway_bill_number} · exp {t.eway_bill_expiry}
                </Link>
              ))}
              {(totalExpired + totalSoon) > 6 && <span>+ {totalExpired + totalSoon - 6} more</span>}
            </div>
          </div>
        </div>
        <button onClick={() => setDismissed(true)} data-testid="eway-banner-dismiss" className="text-current opacity-60 hover:opacity-100">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
